import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Calendar, Clock, MapPin, Mic, Square, Trash2 } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import {
  micErrorMessage,
  micUnavailableMessage,
  VoiceRecorder,
  voiceExtensionFor,
} from '@/lib/media';
import { formatDuration, formatSlot } from '@/lib/format';
import { useUserId } from '@/components/AuthProvider';
import { useFeedback } from '@/components/Feedback';
import { PageHeader } from '@/components/ui';
import Spinner from '@/components/Spinner';
import styles from './ApplyBooking.module.css';

type Booking = {
  id: string;
  address: string;
  description: string | null;
  preferred_date: string | null;
  preferred_time: string | null;
  service_name: string;
};

export default function ApplyBooking() {
  const { bookingId } = useParams<{ bookingId: string }>();
  const navigate = useNavigate();
  const userId = useUserId();
  const { toast } = useFeedback();

  const [booking, setBooking] = useState<Booking | null>(null);
  const [loadingBooking, setLoadingBooking] = useState(true);
  const [message, setMessage] = useState('');
  const [price, setPrice] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [recorder, setRecorder] = useState<VoiceRecorder | null>(null);
  const [voiceBlob, setVoiceBlob] = useState<Blob | null>(null);
  const [seconds, setSeconds] = useState(0);

  useEffect(() => {
    if (!bookingId) return;
    void (async () => {
      const { data } = await supabase
        .from('bookings')
        .select('id, address, description, preferred_date, preferred_time, services(name)')
        .eq('id', bookingId)
        .maybeSingle();

      if (data) {
        const row = data as any;
        setBooking({
          id: row.id,
          address: row.address,
          description: row.description,
          preferred_date: row.preferred_date,
          preferred_time: row.preferred_time,
          service_name: row.services?.name ?? 'Service',
        });
      }
      setLoadingBooking(false);
    })();
  }, [bookingId]);

  // Tick the recording timer.
  useEffect(() => {
    if (!recorder) return;
    const timer = window.setInterval(() => setSeconds((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [recorder]);

  useEffect(() => () => recorder?.cancel(), [recorder]);

  const startRecording = async () => {
    const reason = VoiceRecorder.unavailableReason();
    if (reason) {
      setError(micUnavailableMessage(reason));
      return;
    }
    try {
      const next = new VoiceRecorder();
      await next.start();
      setSeconds(0);
      setRecorder(next);
    } catch (err) {
      setError(micErrorMessage(err));
    }
  };

  const stopRecording = async () => {
    if (!recorder) return;
    try {
      setVoiceBlob(await recorder.stop());
    } catch {
      setError("L'enregistrement a échoué.");
    } finally {
      setRecorder(null);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');

    const priceValue = Number.parseFloat(price);
    if (!price || !Number.isFinite(priceValue) || priceValue <= 0) {
      setError('Veuillez saisir un prix valide.');
      return;
    }
    if (!message.trim() && !voiceBlob) {
      setError('Ajoutez un message ou un message vocal.');
      return;
    }
    if (!userId || !bookingId) return;

    setLoading(true);
    try {
      let voiceUrl: string | null = null;
      if (voiceBlob) {
        const contentType = voiceBlob.type || 'audio/webm';
        const fileName = `voice/${Date.now()}.${voiceExtensionFor(contentType)}`;
        const { data, error: uploadError } = await supabase.storage
          .from('voice-messages')
          .upload(fileName, voiceBlob, { contentType });
        if (!uploadError && data) {
          voiceUrl = supabase.storage.from('voice-messages').getPublicUrl(data.path).data.publicUrl;
        }
      }

      const { error: insertError } = await supabase.from('booking_proposals').insert({
        booking_id: bookingId,
        provider_id: userId,
        message: message.trim() || null,
        voice_url: voiceUrl,
        price: priceValue,
        status: 'pending',
      });

      if (insertError) throw new Error(insertError.message);
      toast('success', 'Candidature envoyée', 'Le client peut maintenant consulter votre proposition.');
      navigate('/pro/requests?tab=available', { replace: true });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Une erreur est survenue.');
    } finally {
      setLoading(false);
    }
  };

  if (loadingBooking) return <Spinner block label="Chargement de la demande..." />;

  return (
    <div>
      <PageHeader title="Postuler" back="/pro/requests?tab=available" />

      <form className={styles.form} onSubmit={handleSubmit} noValidate>
        {booking ? (
          <section className={styles.summary}>
            <h2 className={styles.summaryService}>{booking.service_name}</h2>
            <p className={styles.summaryRow}>
              <MapPin size={15} /> {booking.address}
            </p>
            {booking.preferred_date ? (
              <p className={styles.summaryRow}>
                <Calendar size={15} /> {formatSlot(booking.preferred_date)}
                {booking.preferred_time ? (
                  <>
                    <Clock size={15} style={{ marginLeft: 10 }} /> {booking.preferred_time}
                  </>
                ) : null}
              </p>
            ) : null}
          </section>
        ) : (
          <p className="alert alert--error">Demande introuvable.</p>
        )}

        {error ? (
          <p className="alert alert--error" role="alert">
            {error}
          </p>
        ) : null}

        <section className={styles.section}>
          <h2 className="section-title">Description du client</h2>
          <div className={styles.clientDescription}>
            {booking?.description?.trim() || 'Aucune description fournie par le client.'}
          </div>
        </section>

        <section className={styles.section}>
          <label className="label" htmlFor="message">
            Votre message
          </label>
          <textarea
            id="message"
            className="textarea"
            placeholder="Décrivez votre approche, vos disponibilités..."
            value={message}
            onChange={(event) => setMessage(event.target.value)}
            disabled={loading}
          />
        </section>

        <section className={styles.section}>
          <span className="label">Message vocal (optionnel)</span>
          {voiceBlob ? (
            <div className={styles.voicePreview}>
              <Mic size={19} color="var(--green-deep)" />
              <span className={styles.voiceText}>
                Message enregistré · {formatDuration(seconds)}
              </span>
              <button
                type="button"
                className={styles.voiceDelete}
                onClick={() => {
                  setVoiceBlob(null);
                  setSeconds(0);
                }}
                aria-label="Supprimer le vocal"
              >
                <Trash2 size={18} />
              </button>
            </div>
          ) : recorder ? (
            <button
              type="button"
              className={`${styles.recordBtn} ${styles.recordBtnOn}`}
              onClick={() => void stopRecording()}
            >
              <Square size={18} fill="#fff" /> Arrêter · {formatDuration(seconds)}
            </button>
          ) : (
            <button
              type="button"
              className={styles.recordBtn}
              onClick={() => void startRecording()}
              disabled={loading}
            >
              <Mic size={19} /> Enregistrer un message vocal
            </button>
          )}
        </section>

        <section className={styles.priceSection}>
          <label className={styles.priceLabel} htmlFor="price">
            Votre prix (MAD)
          </label>
          <div className={styles.priceWrap}>
            <input
              id="price"
              className={styles.priceInput}
              type="number"
              min="0"
              step="1"
              placeholder="•••"
              value={price}
              onChange={(event) => setPrice(event.target.value)}
              disabled={loading}
            />
            <span className={styles.priceCurrency}>MAD</span>
          </div>
        </section>

        <button type="submit" className="btn btn--primary btn--block" disabled={loading || !booking}>
          {loading ? <Spinner size={16} color="var(--ink)" /> : 'Envoyer ma candidature'}
        </button>
      </form>
    </div>
  );
}
